import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ChevronDown, Package, Truck, RefreshCw, ShieldCheck, User, RotateCcw, MessageCircle, Mail, Phone, Clock } from 'lucide-react';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { faqCategories, faqItems, getPopularFAQs, searchFAQ } from '../data/faq';
const iconMap: Record<string, any> = {
  Package,
  Truck,
  RefreshCw,
  ShieldCheck,
  User,
  RotateCcw
};
export function FAQPage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const toggleItem = (id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const filteredFAQs = searchQuery ? searchFAQ(searchQuery) : selectedCategory === 'all' ? faqItems : faqItems.filter(item => item.category === selectedCategory);
  const popularFAQs = getPopularFAQs();
  return <div className="min-h-screen bg-gray-50 pb-24" data-id="element-6088">
      {/* Breadcrumb */}
      <div className="container-custom pt-8 pb-4" data-id="element-6089">
        <Breadcrumb items={[{
        label: 'Inicio',
        href: '/'
      }, {
        label: 'Preguntas Frecuentes',
        href: '/faq',
        active: true
      }]} data-id="element-6090" />
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white py-20 relative overflow-hidden" data-id="element-6091">
        <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMSI+PHBhdGggZD0iTTM2IDM0di00aC0ydjRoLTR2Mmg0djRoMnYtNGg0di0yaC00em0wLTMwVjBoLTJ2NGgtNHYyaDR2NGgyVjZoNFY0aC00ek02IDM0di00SDR2NGgwdjJoNHY0aDJ2LTRoNHYtMkg2ek02IDRWMEg0djRIMHYyaDR2NGgyVjZoNFY0SDZ6Ii8+PC9nPjwvZz48L3N2Zz4=')]" data-id="element-6092"></div>

        <div className="container-custom relative z-10" data-id="element-6093">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center max-w-3xl mx-auto" data-id="element-6094">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 font-serif" data-id="element-6095">
              ¿Cómo podemos ayudarte?
            </h1>
            <p className="text-xl text-primary-100 mb-8" data-id="element-6096">
              Encuentra respuestas rápidas a las preguntas más frecuentes sobre
              nuestros productos, envíos y servicios.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto" data-id="element-6097">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-6098" />
              <input type="text" placeholder="Busca tu pregunta..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-4 rounded-2xl border-none shadow-xl text-charcoal-900 placeholder:text-gray-400 focus:ring-2 focus:ring-primary-300 text-base" data-id="element-6099" />
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container-custom mt-12" data-id="element-6100">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-6101">
          {/* Sidebar Categories */}
          <aside className="lg:col-span-1" data-id="element-6102">
            <div className="bg-white rounded-2xl shadow-soft-md p-6 sticky top-28" data-id="element-6103">
              <h3 className="font-bold text-lg mb-4 text-charcoal-900" data-id="element-6104">
                Categorías
              </h3>
              <div className="space-y-2" data-id="element-6105">
                <button onClick={() => setSelectedCategory('all')} className={`w-full text-left px-4 py-3 rounded-xl transition-all ${selectedCategory === 'all' ? 'bg-primary-50 text-primary-800 font-bold' : 'text-charcoal-600 hover:bg-gray-50'}`} data-id="element-6106">
                  Todas las preguntas
                </button>
                {faqCategories.map(category => {
                const Icon = iconMap[category.icon];
                return <button key={category.id} onClick={() => setSelectedCategory(category.id)} className={`w-full text-left px-4 py-3 rounded-xl transition-all flex items-center gap-3 ${selectedCategory === category.id ? 'bg-primary-50 text-primary-800 font-bold' : 'text-charcoal-600 hover:bg-gray-50'}`} data-id="element-6107">
                      <Icon className="w-5 h-5" data-id="element-6108" />
                      <span className="text-sm" data-id="element-6109">{category.name}</span>
                    </button>;
              })}
              </div>

              {/* Contact Card */}
              <div className="mt-8 p-4 bg-primary-50 rounded-xl border border-primary-100" data-id="element-6110">
                <h4 className="font-bold text-primary-900 mb-2 text-sm" data-id="element-6111">
                  ¿No encuentras tu respuesta?
                </h4>
                <p className="text-xs text-primary-700 mb-3" data-id="element-6112">
                  Nuestro equipo está aquí para ayudarte
                </p>
                <a href="/contacto" className="text-xs font-bold text-primary-700 hover:text-primary-900 flex items-center gap-1" data-id="element-6113">
                  Contactar soporte <MessageCircle className="w-3 h-3" data-id="element-6114" />
                </a>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3" data-id="element-6115">
            {/* Popular Questions (only show when no search/filter) */}
            {!searchQuery && selectedCategory === 'all' && <section className="mb-12" data-id="element-6116">
                <div className="flex items-center gap-2 mb-6" data-id="element-6117">
                  <h2 className="text-2xl font-bold text-charcoal-900 font-serif" data-id="element-6118">
                    Preguntas Populares
                  </h2>
                  <Badge variant="primary" size="sm" data-id="element-6119">
                    Más consultadas
                  </Badge>
                </div>
                <div className="space-y-4" data-id="element-6120">
                  {popularFAQs.map(faq => <motion.div key={faq.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="bg-white rounded-2xl shadow-soft-sm border border-gray-100 overflow-hidden" data-id="element-6121">
                      <button onClick={() => toggleItem(faq.id)} className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-6122">
                        <span className="font-bold text-charcoal-900 pr-4" data-id="element-6123">
                          {faq.question}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${expandedItems.has(faq.id) ? 'rotate-180' : ''}`} data-id="element-6124" />
                      </button>
                      <AnimatePresence data-id="element-6125">
                        {expandedItems.has(faq.id) && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} className="overflow-hidden" data-id="element-6126">
                            <div className="px-6 pb-5 text-charcoal-600 leading-relaxed whitespace-pre-line" data-id="element-6127">
                              {faq.answer}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </motion.div>)}
                </div>
              </section>}

            {/* All Questions */}
            <section data-id="element-6128">
              <h2 className="text-2xl font-bold text-charcoal-900 font-serif mb-6" data-id="element-6129">
                {searchQuery ? `Resultados para "${searchQuery}"` : selectedCategory === 'all' ? 'Todas las Preguntas' : faqCategories.find(c => c.id === selectedCategory)?.name}
              </h2>

              {filteredFAQs.length === 0 ? <div className="text-center py-12 bg-white rounded-2xl border border-gray-100" data-id="element-6130">
                  <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-6131" />
                  <h3 className="text-lg font-bold text-charcoal-800 mb-2" data-id="element-6132">
                    No se encontraron resultados
                  </h3>
                  <p className="text-charcoal-500" data-id="element-6133">
                    Intenta con otros términos de búsqueda o contacta con
                    nuestro equipo
                  </p>
                </div> : <div className="space-y-4" data-id="element-6134">
                  {filteredFAQs.map((faq, index) => <motion.div key={faq.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className="bg-white rounded-2xl shadow-soft-sm border border-gray-100 overflow-hidden hover:shadow-soft-md transition-shadow" data-id="element-6135">
                      <button onClick={() => toggleItem(faq.id)} className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-6136">
                        <span className="font-bold text-charcoal-900 pr-4" data-id="element-6137">
                          {faq.question}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${expandedItems.has(faq.id) ? 'rotate-180' : ''}`} data-id="element-6138" />
                      </button>
                      <AnimatePresence data-id="element-6139">
                        {expandedItems.has(faq.id) && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} className="overflow-hidden" data-id="element-6140">
                            <div className="px-6 pb-5 text-charcoal-600 leading-relaxed whitespace-pre-line border-t border-gray-50 pt-4" data-id="element-6141">
                              {faq.answer}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </motion.div>)}
                </div>}
            </section>
          </main>
        </div>
      </div>

      {/* Contact CTA Section */}
      <section className="container-custom mt-20" data-id="element-6142">
        <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-3xl p-12 text-white text-center relative overflow-hidden shadow-2xl" data-id="element-6143">
          <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMSI+PHBhdGggZD0iTTM2IDM0di00aC0ydjRoLTR2Mmg0djRoMnYtNGg0di0yaC00em0wLTMwVjBoLTJ2NGgtNHYyaDR2NGgyVjZoNFY0aC00ek02IDM0di00SDR2NGgwdjJoNHY0aDJ2LTRoNHYtMkg2ek02IDRWMEg0djRIMHYyaDR2NGgyVjZoNFY0SDZ6Ii8+PC9nPjwvZz48L3N2Zz4=')]" data-id="element-6144"></div>

          <div className="relative z-10 max-w-2xl mx-auto" data-id="element-6145">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-serif" data-id="element-6146">
              ¿Necesitas más ayuda?
            </h2>
            <p className="text-lg text-primary-100 mb-8" data-id="element-6147">
              Nuestro equipo de expertos está disponible para resolver cualquier
              duda
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-6148">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20" data-id="element-6149">
                <MessageCircle className="w-8 h-8 mb-3 mx-auto" data-id="element-6150" />
                <h3 className="font-bold mb-2" data-id="element-6151">Chat en Vivo</h3>
                <p className="text-sm text-primary-100 mb-3" data-id="element-6152">
                  Respuesta inmediata
                </p>
                <button className="text-sm font-bold hover:underline" data-id="element-6153">
                  Iniciar chat →
                </button>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20" data-id="element-6154">
                <Mail className="w-8 h-8 mb-3 mx-auto" data-id="element-6155" />
                <h3 className="font-bold mb-2" data-id="element-6156">Email</h3>
                <p className="text-sm text-primary-100 mb-3" data-id="element-6157">
                  Respuesta en 24h
                </p>
                <a href="mailto:hola@petiora.com" className="text-sm font-bold hover:underline" data-id="element-6158">
                  hola@petiora.com →
                </a>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20" data-id="element-6159">
                <Phone className="w-8 h-8 mb-3 mx-auto" data-id="element-6160" />
                <h3 className="font-bold mb-2" data-id="element-6161">Teléfono</h3>
                <p className="text-sm text-primary-100 mb-3" data-id="element-6162">L-V 9:00-18:00</p>
                <a href="tel:+34900123456" className="text-sm font-bold hover:underline" data-id="element-6163">
                  900 123 456 →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}